/**
 * Genera el HTML para imprimir un paquete turístico
 * @param paquete - El paquete seleccionado con todos sus datos
 * @param formatearMoneda - Función para formatear valores monetarios
 * @returns HTML string listo para imprimir
 */
export const generatePackagePrintHTML = (
  paquete: any,
  formatearMoneda: (valor: number, moneda: string) => string
): string => {
  if (!paquete) return '';

  const p = paquete;
  const fechaFormateada = p.fechaVenta 
    ? new Date(p.fechaVenta).toLocaleDateString('es-AR') 
    : new Date().toLocaleDateString('es-AR');
  
  // Generar filas de servicios (aéreos, hoteles, extras)
  const servicios = p.servicios || [];
  const serviciosHTML = servicios
    .filter((s: any) => (s.precioVenta || 0) > 0)
    .map((s: any, idx: number) => `
      <tr>
        <td style="padding: 8px; text-align: center; border: 1px solid #ddd;">${idx + 1}</td>
        <td style="padding: 8px; border: 1px solid #ddd;">${s.tipo || 'Servicio'}</td>
        <td style="padding: 8px; border: 1px solid #ddd;">${s.destino || '-'}</td>
        <td style="padding: 8px; border: 1px solid #ddd;">${s.descripcion || '-'}</td>
        <td style="padding: 8px; text-align: right; border: 1px solid #ddd;">${formatearMoneda(s.precioVenta || 0, p.moneda)}</td>
      </tr>
    `).join('');
  
  const condicionesVenta = `Tarifa total final en base según se indica, sujeta a modificaciones sin previo aviso al momento de la efectiva contratación.
La tarifa total incluye impuestos y gastos de gestión.
Todos los servicios contratados deberán ser cancelados con al menos 3 días de anticipación a la fecha de salida. En caso de no cancelación, la agencia no está obligada a prestar los servicios presupuestados.
La empresa no se responsabiliza por el estado de mantenimiento de los hoteles y/o transportes.
Los servicios contratados no permiten cambios ni devoluciones, salvo que se indique lo contrario.
En algunos casos, pueden existir tasas locales de alojamiento y/o traslados de pago directo no contempladas en la tarifa total, que podrían aplicarse en algunos destinos, y cuyo importe dependerá de la ciudad y el número de noches de estadía.
Los traslados, si los hubiere, son en servicio regular y compartido, salvo que se indique lo contrario.
Cotización NO implica reserva de ninguno de los servicios arriba mencionados.`;

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Paquete #${p.id} - ${p.cliente}</title>
      <style>
        @page { size: A4; margin: 15mm; }
        body { font-family: Arial, sans-serif; font-size: 11px; color: #333; }
        .header { text-align: center; margin-bottom: 20px; border-bottom: 3px solid #c2185b; padding-bottom: 15px; }
        .header img { height: 50px; margin-bottom: 10px; }
        .header h1 { color: #c2185b; margin: 5px 0; font-size: 20px; }
        .header p { margin: 3px 0; color: #666; }
        .header .paquete-id { font-size: 14px; color: #c2185b; font-weight: bold; }
        .section { margin: 15px 0; }
        .section-title { background: #c2185b; color: white; padding: 6px 12px; font-weight: bold; margin-bottom: 8px; }
        .detalle-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; padding: 10px; background: #f9f9f9; border: 1px solid #ddd; }
        .detalle-item { display: flex; justify-content: space-between; }
        .detalle-label { font-weight: bold; }
        table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        th { background: #f5f5f5; padding: 8px; text-align: left; border: 1px solid #ddd; font-weight: bold; }
        .condiciones { background: #fff3e0; border: 1px solid #ff9800; padding: 12px; font-size: 9px; line-height: 1.5; margin-top: 15px; }
        .condiciones-title { font-weight: bold; color: #e65100; margin-bottom: 8px; }
        .total-box { text-align: right; margin-top: 20px; font-size: 18px; font-weight: bold; }
        .footer { margin-top: 30px; padding-top: 15px; border-top: 3px solid #c2185b; display: flex; justify-content: space-between; font-size: 10px; }
        .footer-left { }
        .footer-right { text-align: right; }
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style>
    </head>
    <body>
      <div class="header">
        <img src="${typeof window !== 'undefined' ? window.location.origin : ''}/logo.png" alt="Logo" />
        <h1>${p.cliente || 'Cliente'}</h1>
        <p>${p.concepto || ''} | ${fechaFormateada}</p>
        <p class="paquete-id"># ${p.id}</p>
      </div>

      <div class="section">
        <div class="section-title">PASAJEROS</div>
        ${(p.pasajerosLista && p.pasajerosLista.length > 0) ? `
        <table>
          <thead>
            <tr>
              <th style="width: 10%; text-align: center;">#</th>
              <th style="width: 60%;">Nombre</th>
              <th style="width: 30%;">DNI/Pasaporte</th>
            </tr>
          </thead>
          <tbody>
            ${p.pasajerosLista.map((pax: any, idx: number) => `
              <tr>
                <td style="padding: 8px; text-align: center; border: 1px solid #ddd;">${idx + 1}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${pax.nombre || ''}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${pax.dni || pax.pasaporte || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        ` : '<div style="padding: 10px; background: #f9f9f9; border: 1px solid #ddd; text-align: center;">Sin pasajeros registrados</div>'}
      </div>

      <div class="section">
        <div class="section-title">DETALLE DEL PAQUETE</div>
        <table>
          <thead>
            <tr>
              <th style="width: 5%; text-align: center;">#</th>
              <th style="width: 15%;">Tipo</th>
              <th style="width: 20%;">Destino</th>
              <th style="width: 40%;">Descripción</th>
              <th style="width: 20%; text-align: right;">Precio</th>
            </tr>
          </thead>
          <tbody>
            ${serviciosHTML || '<tr><td colspan="5" style="padding: 8px; text-align: center; border: 1px solid #ddd;">Sin servicios</td></tr>'}
          </tbody>
        </table>
      </div>

      ${p.descripcion ? `
      <div class="section">
        <div class="section-title">DESCRIPCIÓN</div>
        <div style="padding: 10px; background: #f9f9f9; border: 1px solid #ddd;">${p.descripcion}</div>
      </div>
      ` : ''}

      <div class="condiciones">
        <div class="condiciones-title">CONDICIONES DE VENTA</div>
        ${condicionesVenta.split('\n').map(line => `<div>${line}</div>`).join('')}
      </div>

      <div class="total-box">
        TOTAL: ${formatearMoneda(p.precioVenta || 0, p.moneda)}
      </div>

      <div class="footer">
        <div class="footer-left">
          <strong>Turismo Vittorio SRL</strong><br/>
          Legajo Nº 1881<br/>
          CUIT Nº 30-61632343-8
        </div>
        <div class="footer-right">
          <strong>(0264) 4204000</strong><br/>
          Sarmiento 174 (sur), Capital - San Juan
        </div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Abre una ventana de impresión con el HTML del paquete
 */
export const printPackage = (
  paquete: any,
  formatearMoneda: (valor: number, moneda: string) => string
): void => {
  const html = generatePackagePrintHTML(paquete, formatearMoneda);
  if (!html) return;
  
  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  }
};
